"use client";
import { useMutation } from "@tanstack/react-query";
import { useAppDispatch } from "@/redux/hooks";
import { addOrUpdateItemInCart } from "@/redux/features/cart/cartSlice";
import { useSnackbar } from "notistack";
import getError from "@/lib/getError";
import { sendAddToCartEvent } from "@/app/(main)/products/[...slugs]/sendAddToCartEvent";
import { useProductPageContext } from "./useProductPageContext";
import { CartPricingMode } from "@/__generated__/sdk";
import { useCart } from "@/redux/features/cart/hooks";
import { toPricingMode, PRICING_MODE_LABEL } from "@/lib/pricingMode";
import { effectiveSellerPrice, findCompatibleSellerOffer, getVariantSellerOffers } from "../sellerOffers";
import { PricingModeGuideRequest } from "../components/CartModeGuideDialog";

function isMixedPricingError(message: unknown) {
  return (
    typeof message === "string" &&
    (message.includes("نقدی و اعتباری") || message.includes("هم‌زمان") || message.includes("هم زمان"))
  );
}

export function useCartVariantActions(productId?: string, variantId?: number, maxBasketSize: number = 0) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { onAddToCart, variants, openPricingModeGuide } = useProductPageContext();
  const cart = useCart();

  const explainModeMismatch = (quantity: number, pricingMode: CartPricingMode, cartMode: CartPricingMode) => {
    if (!variantId || !productId) return;
    const variant = variants.find((item) => item.id === variantId);
    const clickedOffer = getVariantSellerOffers(variant).find((offer) => offer.mode === pricingMode);
    const suggestion = findCompatibleSellerOffer(variants, variantId, cartMode);
    const request: PricingModeGuideRequest = {
      cartMode,
      quantity,
      productId,
        clicked: clickedOffer
        ? {
            label: clickedOffer.label,
            mode: clickedOffer.mode,
            price: effectiveSellerPrice(clickedOffer),
            variantId: clickedOffer.variantId,
            maxBasket: Number(clickedOffer.variant.offer?.maxBasketCount ?? clickedOffer.variant.maxBasketCount ?? 0),
            shippingOrigin: clickedOffer.shippingOriginLabel,
          }
        : {
            label: PRICING_MODE_LABEL[pricingMode],
            mode: pricingMode,
            price: variant?.price ?? 0,
            variantId,
          },
      suggestion: suggestion
        ? {
            label: suggestion.label,
            mode: suggestion.mode,
            price: effectiveSellerPrice(suggestion),
            variantId: suggestion.variantId,
            maxBasket: Number(suggestion.variant.offer?.maxBasketCount ?? suggestion.variant.maxBasketCount ?? 0),
            shippingOrigin: suggestion.shippingOriginLabel,
          }
        : null,
    };
    openPricingModeGuide(request);
  };

  const { mutateAsync: updateCart, isPending } = useMutation({
    mutationFn: async ({ quantity, pricingMode }: { quantity: number; pricingMode?: CartPricingMode }) => {
      return dispatch(
        addOrUpdateItemInCart({
          createCartInput: {
            quantity,
            variantId: variantId!,
            productId: productId!,
            // when omitted the thunk falls back to the current cart's mode
            ...(pricingMode ? { pricingMode } : {}),
          },
        }),
      ).unwrap();
    },
    onError: (error: unknown, variables) => {
      const message = getError(error);
      if (isMixedPricingError(message) && variables.pricingMode && variables.quantity > 0) {
        const cartMode = variables.pricingMode === CartPricingMode.Credit ? CartPricingMode.Cash : CartPricingMode.Credit;
        explainModeMismatch(variables.quantity, variables.pricingMode, cartMode);
        return;
      }
      enqueueSnackbar(message, { variant: "error", autoHideDuration: 5000 });
    },
    onSuccess: (data) => {
      const item = data.cartItems.find((a) => a.variantId === variantId && a.productId === productId);
      if (item) sendAddToCartEvent(item);
    },
  });

  const handleQuantityChange = (quantity: number, pricingMode?: CartPricingMode) => {
    const execute = () => {
      if (!variantId || !productId) {
        enqueueSnackbar("تنوع محصول انتخاب نشده است", { variant: "error" });
        return;
      }

      if (quantity <= 0) {
        updateCart({ quantity: 0, pricingMode });
        return;
      }

      if (quantity > maxBasketSize) {
        enqueueSnackbar("موجودی کافی نیست", { variant: "warning" });
        return;
      }

      const itemCount = cart.cart?.cartItems?.reduce((sum, item) => sum + Number(item.quantity || 0), 0) ?? 0;
      const lockedMode = itemCount > 0 ? toPricingMode(cart.pricingMode ?? cart.cart?.pricingMode) : null;
      if (pricingMode && lockedMode && pricingMode !== lockedMode) {
        explainModeMismatch(quantity, pricingMode, lockedMode);
        return;
      }

      updateCart({ quantity, pricingMode });
    };

    onAddToCart(execute);
  };

  return { handleQuantityChange, isPending };
}
